import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import moment from 'moment-timezone';

import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Row, Col, Select, Tag } from 'antd';

import {
  usNPPsSelector,
  usNPPSelector,
  loadUsNppUnavailabilities,
  loadUsNuclearOutage,
  loadUsNppMonthlyProduction,
  loadUsNppAllForYear,
} from '../../store/usNPPs';
import Link from '../../components/Link';

import MonthlyProductionChart from './MonthlyProductionChart';
import FleetMonthlyAvailabilityChart from './FleetMonthlyAvailabilityChart';
import AvailabilitySection from './AvailabilitySection';

import './index.css';
import { loadUsNppSnapshot } from '../../store/usNPPs';
import { getCurrentDate as getCurrentDateSelector } from '../../store/otherSelectors';
import { getElapsedHoursInCurrentMonth } from '../../utils/timeUtils';

function USNPPView(props) {
  const {
    currentDate,
    usNPPs,
    currentUSNPP,
    goTo,
    usNPPId,
    dispatch,
    production,
    unavailabilities,
    outage,
  } = props;

  const [currentYear, setCurrentYear] = useState(moment().year());
  const snapshot = useSelector(state => state.usNPPs?.snapshot || null);
  const loadedData = useSelector(state => state.usNPPs?.loadedData || {});
  const productionByPlant = useSelector(
    state => state.usNPPs?.productionByPlant || {},
  );

  // Add loading states to prevent re-triggering failed API calls
  const { loading: usNPPsLoading, errors: usNPPsErrors } = props;

  // Track loaded data to prevent duplicate requests
  const loadedDataRef = React.useRef({
    unavailabilities: {},
    outage: {},
    production: {},
  });

  // Track if we've initialized data fetching
  const hasInitializedRef = React.useRef(false);

  // Preload and cache all US NPP data for the year once; no per-plant fetching needed
  useEffect(() => {
    if (usNPPsLoading) return;
    dispatch(
      loadUsNppAllForYear({
        year: currentYear,
        date: moment(currentDate).format('YYYY-MM-DD'),
      }),
    );
  }, [dispatch, currentYear, currentDate, usNPPsLoading]);

  // Prefetch monthly production for all plants when viewing the global US NPP page
  useEffect(() => {
    // Skip noisy per-plant prefetch; we use the global yearly dataset
    const hasGlobalProd = !!loadedData?.global?.[currentYear]?.production;
    if (currentUSNPP) return;
    if (!hasGlobalProd) return; // global loader handles fetching
  }, [currentUSNPP, loadedData, currentYear]);

  // Load global snapshot (French-style counts) for the selected date
  useEffect(() => {
    dispatch(
      loadUsNppSnapshot({ date: moment(currentDate).format('YYYY-MM-DD') }),
    );
  }, [dispatch, currentDate]);

  // When a plant is selected, fetch per-plant facility-level unavailabilities for the current year (if not loaded)
  useEffect(() => {
    if (!currentUSNPP) return;
    const plantKey = String(currentUSNPP.id);
    const hasPlantYear = !!loadedData?.[plantKey]?.[currentYear]
      ?.unavailabilities;
    if (!hasPlantYear) {
      dispatch(
        loadUsNppUnavailabilities({
          year: currentYear,
          plantId: currentUSNPP.id,
        }),
      );
    }
  }, [currentUSNPP, currentYear, loadedData, dispatch]);

  // Resolve production for the current view (prefer per-plant/year cached data)
  const productionForView = useMemo(() => {
    if (currentUSNPP) {
      const plantKey = String(currentUSNPP.id);
      const yearData = loadedData?.[plantKey]?.[currentYear];
      const prod = yearData?.production || [];
      return prod;
    }
    return production || [];
  }, [currentUSNPP, currentYear, loadedData, production]);

  // Resolve unavailabilities for the view (avoid using global top-level that may be overwritten)
  const unavailabilitiesForView = useMemo(() => {
    if (currentUSNPP) {
      const plantKey = String(currentUSNPP.id);
      const yearData = loadedData?.[plantKey]?.[currentYear];
      return yearData?.unavailabilities || unavailabilities || [];
    }
    const yearData = loadedData?.global?.[currentYear];
    return yearData?.unavailabilities || unavailabilities || [];
  }, [currentUSNPP, currentYear, loadedData, unavailabilities]);

  // Process chart data for the chart
  // For global view, we use outage data; for plant view, we use production data
  const chartData = useMemo(() => {
    const selYear = moment(currentDate).year();
    const selMonth = moment(currentDate).month() + 1; // 1..12
    if (!currentUSNPP) {
      // Global view: infer monthly production from daily outage series
      if (!outage || !Array.isArray(outage)) return {};

      const dataByYear = {};
      // aggregate: sum over days per month => (capacity - outage)*24 MWh
      outage.forEach(item => {
        const date = moment(item.period);
        const y = date.year();
        const m = date.month() + 1;
        const capacity = parseFloat(item.capacity) || 0;
        const outageVal = parseFloat(item.outage) || 0;
        const dailyMWh = Math.max(0, capacity - outageVal) * 24;

        if (!dataByYear[y]) dataByYear[y] = [];
        const monthData = dataByYear[y].find(d => d.month === m);
        if (monthData) {
          monthData.production = (monthData.production || 0) + dailyMWh;
        } else {
          dataByYear[y].push({ month: m, production: dailyMWh });
        }
      });

      Object.entries(dataByYear).forEach(([y, yearArr]) => {
        yearArr.sort((a, b) => a.month - b.month);
        if (Number(y) === Number(selYear)) {
          dataByYear[y] = yearArr.filter(d => Number(d.month) <= selMonth);
        } else {
          dataByYear[y] = yearArr;
        }
      });

      return dataByYear;
    } else {
      // Plant view: prefer per-plant monthly series from store; else fallback to global production filtered; else outages
      const series =
        productionByPlant?.[currentYear]?.[String(currentUSNPP.id)] || null;
      if (Array.isArray(series) && series.length > 0) {
        try {
          const nonZero = series
            .filter(m => Number(m.production) > 0)
            .map(m => m.month);
          // eslint-disable-next-line no-console
          console.info('[USNPPView] Chart series (store)', {
            plantId: String(currentUSNPP.id),
            year: currentYear,
            months: series.length,
            nonZero,
          });
        } catch (e) {}
        const obj = { [currentYear]: series };
        if (Number(currentYear) === Number(selYear)) {
          obj[currentYear] = (obj[currentYear] || []).filter(
            d => Number(d.month) <= selMonth,
          );
        }
        return obj;
      }
      // Fallback to global production filtered by plant
      const hasPlantProd =
        productionForView &&
        ((Array.isArray(productionForView) && productionForView.length > 0) ||
          (productionForView.response &&
            productionForView.response.data &&
            productionForView.response.data.length > 0));

      // 1) Try plant-specific production (if present in store)
      let productionArray = hasPlantProd
        ? productionForView?.response?.data || productionForView
        : null;

      // 2) Else, try global production filtered by this plant
      if (!productionArray) {
        const globalProd = loadedData?.global?.[currentYear]?.production || {};
        const arr = globalProd?.response?.data || globalProd;
        if (Array.isArray(arr)) productionArray = arr;
      }

      if (Array.isArray(productionArray)) {
        const dataByYear = {};
        productionArray.forEach(item => {
          // Only process nuclear production data (handle different field names)
          const fuel = item.fuel2002 || item.fuelType || item.fuel;
          if (fuel && fuel !== 'NUC') return;
          // Avoid double counting: accept aggregated (ALL) or ST; prefer ST if both exist
          const primeMover =
            item.primeMover || item.prime_mover || item['prime-mover'];
          if (primeMover && !['ST', 'ALL'].includes(String(primeMover))) return;
          // Filter by plant if a plant is selected (handle different field names)
          const plantCode =
            item.plantCode || item.facility || item.plantId || item.plantid;
          if (String(plantCode) !== String(currentUSNPP.id)) return;
          const date = moment(item.period);
          const year = date.year();
          const month = date.month() + 1; // moment months are 0-indexed
          if (!dataByYear[year]) dataByYear[year] = [];
          const monthData = dataByYear[year].find(d => d.month === month);
          const generation = Math.max(0, parseFloat(item.generation) || 0);
          if (monthData)
            monthData.production = (monthData.production || 0) + generation;
          else dataByYear[year].push({ month, production: generation });
        });
        // Ensure months 1..lastMonth are present (pad zeros for missing months)
        Object.keys(dataByYear).forEach(y => {
          const arr = dataByYear[y];
          if (!arr || arr.length === 0) return;
          const lastMonth = arr.reduce((m, d) => Math.max(m, d.month), 1);
          const byMonth = Object.fromEntries(
            arr.map(d => [d.month, d.production]),
          );
          const filled = [];
          for (let m = 1; m <= lastMonth; m += 1) {
            filled.push({ month: m, production: Number(byMonth[m] || 0) });
          }
          filled.sort((a, b) => a.month - b.month);
          const trimmed =
            Number(y) === Number(selYear)
              ? filled.filter(d => Number(d.month) <= selMonth)
              : filled;
          dataByYear[y] = trimmed;
        });
        try {
          const yKey = Object.keys(dataByYear)[0];
          const arr = yKey ? dataByYear[yKey] : [];
          const nonZero = (arr || [])
            .filter(m => Number(m.production) > 0)
            .map(m => m.month);
          // eslint-disable-next-line no-console
          console.info('[USNPPView] Chart series (fallback global)', {
            plantId: String(currentUSNPP.id),
            year: currentYear,
            months: (arr || []).length,
            nonZero,
          });
        } catch (e) {}
        return dataByYear;
      }

      // 3) Final fallback: build monthly production from daily generator outages (unavailabilities)
      if (
        !Array.isArray(unavailabilitiesForView) ||
        unavailabilitiesForView.length === 0
      )
        return {};
      const facilityId = String(currentUSNPP.id);
      const byDay = {};
      unavailabilitiesForView
        .filter(item => String(item.facility) === facilityId)
        .forEach(item => {
          const date = moment(item.period);
          const year = date.year();
          const m = date.month() + 1;
          const cap = Number(item.capacity) || 0;
          const out = Number(item.outage) || 0;
          const avail = Math.max(0, cap - out);
          const dailyMWh = avail * 24;
          if (!byDay[year]) byDay[year] = {};
          byDay[year][m] = (byDay[year][m] || 0) + dailyMWh;
        });
      const dataByYear = {};
      Object.keys(byDay).forEach(y => {
        const arr = Object.keys(byDay[y])
          .map(m => ({ month: Number(m), production: byDay[y][m] }))
          .sort((a, b) => a.month - b.month);
        dataByYear[y] =
          Number(y) === Number(selYear)
            ? arr.filter(d => Number(d.month) <= selMonth)
            : arr;
      });
      try {
        const yKey = Object.keys(dataByYear)[0];
        const arr = yKey ? dataByYear[yKey] : [];
        const nonZero = (arr || [])
          .filter(m => Number(m.production) > 0)
          .map(m => m.month);
        // eslint-disable-next-line no-console
        console.info('[USNPPView] Chart series (fallback outages)', {
          plantId: String(currentUSNPP.id),
          year: currentYear,
          months: (arr || []).length,
          nonZero,
        });
      } catch (e) {}
      return dataByYear;
    }
  }, [
    currentUSNPP,
    production,
    outage,
    productionByPlant,
    currentYear,
    unavailabilitiesForView,
    currentDate,
  ]);

  // Process availability data
  const availabilityData = useMemo(() => {
    if (!unavailabilitiesForView || !Array.isArray(unavailabilitiesForView))
      return [];
    const dateStr = moment(currentDate).format('YYYY-MM-DD');

    if (!currentUSNPP) {
      // Global view: aggregate by plant (facility) for the selected day; show plants with any outage
      const byPlant = {};
      unavailabilitiesForView
        .filter(
          it =>
            it &&
            typeof it.period === 'string' &&
            it.period.startsWith(dateStr),
        )
        .forEach(it => {
          const fid = String(it.facility || '');
          if (!fid) return;
          const cap = Number(it.capacity) || 0;
          const out = Number(it.outage) || 0;
          if (!byPlant[fid]) byPlant[fid] = { cap: 0, out: 0 };
          byPlant[fid].cap += cap;
          byPlant[fid].out += out;
        });
      const plantNameById = Object.fromEntries(
        (usNPPs || []).map(p => [String(p.id), p.name]),
      );
      return Object.entries(byPlant)
        .filter(([, v]) => v.out > 0)
        .map(([fid, v]) => ({
          id: `${fid}_${dateStr}`,
          plantName: plantNameById[fid] || fid,
          startDate: dateStr,
          endDate: dateStr,
          availableCapacity: Math.max(0, v.cap - v.out),
          unavailableCapacity: v.out,
        }));
    }

    // Plant view: filter by plant and include all days (outage may be zero)
    return unavailabilitiesForView
      .filter(item => String(item.facility) === String(currentUSNPP.id))
      .map(item => {
        const capacity = parseFloat(item.capacity);
        const outageVal = parseFloat(item.outage);
        return {
          id: `${currentUSNPP.id}_${item.generator || 'N/A'}_${item.period}`,
          reactorName: item.generator ? String(item.generator) : 'N/A',
          startDate: item.period,
          endDate: item.period,
          status: 'Unknown',
          availableCapacity:
            isFinite(capacity) && isFinite(outageVal)
              ? Math.max(0, capacity - outageVal)
              : null,
          unavailableCapacity: isFinite(outageVal)
            ? Math.max(0, outageVal)
            : null,
        };
      });
  }, [unavailabilitiesForView, currentUSNPP, currentDate, usNPPs]);

  // Show error message if there are errors loading data
  if (usNPPsErrors) {
    return (
      <div className="USNPPView">
        <Row>
          <Col span={24}>
            <div className="USNPPView__error">
              <h3>Error Loading Data</h3>
              <p>
                Failed to load US Nuclear Power Plant data. Please try again
                later.
              </p>
              <p>
                Error details: {usNPPsErrors.message || usNPPsErrors.toString()}
              </p>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  // If no US NPP is selected, show a list of US NPPs and global data
  if (!currentUSNPP && usNPPs.length > 0) {
    return (
      <div className="USNPPView">
        <Row>
          <Col span={24}>
            <div className="USNPPView__firstRow">
              <div className="USNPPView__firstRow__left">
                <Select
                  placeholder="Select a plant"
                  size="large"
                  onChange={id => goTo(`/us-npp/${id}`)}
                  style={{ width: '10em' }}
                  value={null} // Explicitly set value to null when no US NPP is selected
                >
                  {usNPPs.map(usNPP => (
                    <Select.Option key={usNPP.id} value={usNPP.id}>
                      {usNPP.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="USNPPView__schema">
              <div>
                US Nuclear Power Plants: Click on a plant in the map to view its
                details.
              </div>
            </div>

            {/* Global capacity from CSV (sum of plant capacities) */}
            <div className="USNPPView__details">
              {(() => {
                const totalCapMW = Array.isArray(usNPPs)
                  ? usNPPs.reduce((s, p) => s + (Number(p.capacityMW) || 0), 0)
                  : 0;
                const fmtGW = mw => `${(mw / 1000).toFixed(1)} GW`;
                return <p>Total design capacity: {fmtGW(totalCapMW)}</p>;
              })()}
            </div>

            {/* Global monthly production chart */}
            <div className="USNPPView__production">
              <h3>US Monthly Production</h3>
              <MonthlyProductionChart
                productionData={chartData}
                year={currentYear}
                onYearChange={setCurrentYear}
                integerOnly
                capacityMW={(() =>
                  Array.isArray(usNPPs)
                    ? usNPPs.reduce(
                        (s, p) => s + (Number(p.capacityMW) || 0),
                        0,
                      )
                    : 0)()}
              />
            </div>

            {/* Global monthly capacity & availability over the fleet */}
            <div
              className="USNPPView__production"
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              <h3>US Monthly Capacity & Availability</h3>
              <FleetMonthlyAvailabilityChart
                year={currentYear}
                outageDays={
                  loadedData?.global?.[currentYear]?.outage || outage || []
                }
                productionByMonth={chartData[currentYear] || []}
                nominalCapacityMW={(() =>
                  Array.isArray(usNPPs)
                    ? usNPPs.reduce(
                        (s, p) => s + (Number(p.capacityMW) || 0),
                        0,
                      )
                    : 0)()}
                currentDate={currentDate}
              />
            </div>
            <div className="USNPPView__disclaimer">
              <p>
                This view is experimental and only show monthly production data.{' '}
                <br />
                Sources: EIA APIs (https://www.eia.gov/)
              </p>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  // If a US NPP is selected, show its details
  if (currentUSNPP) {
    return (
      <div className="USNPPView">
        <Row>
          <Col span={24}>
            <div className="USNPPView__firstRow">
              <div className="USNPPView__firstRow__left">
                <Select
                  value={currentUSNPP.id}
                  size="large"
                  onChange={id => goTo(`/us-npp/${id}`)}
                  style={{ width: '10em' }}
                >
                  {usNPPs.map(usNPP => (
                    <Select.Option key={usNPP.id} value={usNPP.id}>
                      {usNPP.name}
                    </Select.Option>
                  ))}
                </Select>
                <span>
                  <Tag style={{ marginLeft: '8px' }}>{currentUSNPP.state}</Tag>
                </span>
              </div>
            </div>

            {/* Compact plant card */}
            {(() => {
              // Capacity
              let capMW = Number(currentUSNPP?.capacityMW || 0) || 0;
              if (capMW <= 0 && Array.isArray(unavailabilities)) {
                const plantItems = unavailabilities.filter(
                  i => String(i.facility) === String(currentUSNPP.id),
                );
                if (plantItems.length) {
                  const byDay = {};
                  plantItems.forEach(i => {
                    const d = i.period;
                    const cap = Number(i.capacity) || 0;
                    byDay[d] = (byDay[d] || 0) + cap;
                  });
                  capMW =
                    Object.values(byDay).reduce((m, v) => Math.max(m, v), 0) ||
                    0;
                }
              }
              const months = chartData[currentYear] || [];
              const totalGenMWh = months.reduce(
                (s, d) => s + (Number(d.production) || 0),
                0,
              );
              // Use hours up to end of last available month in the trimmed series
              const lastMonth = months.length
                ? Number(months[months.length - 1].month)
                : null;
              const hoursYTD = lastMonth
                ? (() => {
                    let totalHours = 0;
                    // Add hours for complete months
                    for (let m = 1; m < lastMonth; m++) {
                      totalHours += new Date(currentYear, m, 0).getDate() * 24;
                    }
                    // For the last month, check if it's current month and use elapsed hours
                    const now = moment();
                    const isCurrentMonth =
                      now.year() === currentYear &&
                      now.month() + 1 === lastMonth;
                    if (isCurrentMonth) {
                      totalHours += getElapsedHoursInCurrentMonth(
                        currentYear,
                        lastMonth,
                      );
                    } else {
                      totalHours +=
                        new Date(currentYear, lastMonth, 0).getDate() * 24;
                    }
                    return totalHours;
                  })()
                : 0;
              const avgPowerMW = hoursYTD > 0 ? totalGenMWh / hoursYTD : 0;
              const avgLoadPct =
                capMW > 0 ? Math.round((avgPowerMW / capMW) * 100) : null;
              const fmtCap = mw =>
                mw >= 1000
                  ? `${(mw / 1000).toFixed(1)} GW`
                  : `${Math.round(mw)} MW`;
              const fmtPower = mw =>
                mw >= 1000
                  ? `${(mw / 1000).toFixed(1)} GW`
                  : `${Math.round(mw)} MW`;
              return (
                <div className="USNPPView__plantCard">
                  <div className="USNPPView__plantCard__title">Plant Card</div>
                  <div className="USNPPView__plantCard__grid">
                    <div className="USNPPView__plantCard__item">
                      <span className="USNPPView__plantCard__label">Name:</span>
                      {currentUSNPP.name}
                    </div>
                    <div className="USNPPView__plantCard__item">
                      <span className="USNPPView__plantCard__label">
                        State:
                      </span>
                      {currentUSNPP.state}
                    </div>
                    <div className="USNPPView__plantCard__item">
                      <span className="USNPPView__plantCard__label">
                        Capacity:
                      </span>
                      {fmtCap(capMW)}
                    </div>
                    <div className="USNPPView__plantCard__item">
                      <span className="USNPPView__plantCard__label">
                        Average power this year:
                      </span>
                      {fmtPower(avgPowerMW)}
                    </div>
                    <div className="USNPPView__plantCard__item USNPPView__plantCard__item--wide">
                      <span className="USNPPView__plantCard__label">
                        Average load:
                      </span>
                      {avgLoadPct !== null ? `${avgLoadPct}%` : 'N/A'}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Plant-specific monthly production chart */}
            <div className="USNPPView__production">
              <h3>Monthly Production</h3>
              {(() => {
                // Determine plant capacity to enable per-month load in tooltip
                let capMW = Number(currentUSNPP?.capacityMW || 0) || 0;
                if (capMW <= 0 && Array.isArray(unavailabilities)) {
                  const plantItems = unavailabilities.filter(
                    i => String(i.facility) === String(currentUSNPP.id),
                  );
                  if (plantItems.length) {
                    const byDay = {};
                    plantItems.forEach(i => {
                      const d = i.period;
                      const cap = Number(i.capacity) || 0;
                      byDay[d] = (byDay[d] || 0) + cap;
                    });
                    capMW =
                      Object.values(byDay).reduce(
                        (m, v) => Math.max(m, v),
                        0,
                      ) || 0;
                  }
                }
                return (
                  <MonthlyProductionChart
                    productionData={chartData}
                    year={currentYear}
                    onYearChange={setCurrentYear}
                    integerOnly
                    capacityMW={capMW}
                  />
                );
              })()}
            </div>

            {/* Plant-specific availability */}
            <div className="USNPPView__availability">
              <AvailabilitySection
                availabilityData={availabilityData}
                currentDate={currentDate}
              />
            </div>
            <div>{/* Add some spacing */}&nbsp;</div>
            <div className="USNPPView__disclaimer">
              <p>
                This view is experimental and only show monthly production data.{' '}
                <br />
                Sources: EIA APIs (https://www.eia.gov/)
              </p>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  // If no US NPPs are available, show a message
  return (
    <div className="USNPPView">
      <Row>
        <Col span={24}>
          <div className="USNPPView__schema">
            <div>No US Nuclear Power Plants Available</div>
          </div>
        </Col>
      </Row>
    </div>
  );
}

USNPPView.propTypes = {
  currentDate: PropTypes.string.isRequired,
  usNPPs: PropTypes.array.isRequired,
  currentUSNPP: PropTypes.object,
  goTo: PropTypes.func.isRequired,
  usNPPId: PropTypes.string.isRequired,
  production: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  unavailabilities: PropTypes.array,
  outage: PropTypes.array,
};

function USNPPViewWithRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { usNPPId } = params;
  const currentDate = getCurrentDateSelector(location);

  const goTo = (url, hash = '') => navigate(`${url}${location.search}${hash}`);

  return (
    <USNPPViewConnected
      usNPPId={usNPPId}
      currentDate={currentDate}
      goTo={goTo}
      location={location}
      params={params}
    />
  );
}

// Create a factory function to generate stable mapStateToProps
const makeMapStateToProps = () => {
  // Create memoized selectors that remember their parameters
  const getUsNPPs = createSelector(
    [state => state],
    state => usNPPsSelector(state),
  );

  const getCurrentUSNPP = createSelector(
    [state => state, (state, props) => props.usNPPId],
    (state, usNPPId) => usNPPSelector(state, { usNPPId }),
  );

  const getProduction = createSelector(
    [state => state],
    state => state.usNPPs?.production || [],
  );
  const getUnavailabilities = createSelector(
    [state => state],
    state => state.usNPPs?.unavailabilities || [],
  );
  const getOutage = createSelector(
    [state => state],
    state => state.usNPPs?.outage || [],
  );
  const getLoading = createSelector(
    [state => state],
    state => state.usNPPs?.loading || false,
  );
  const getErrors = createSelector(
    [state => state],
    state => state.usNPPs?.errors || null,
  );

  return (state, props) => {
    const { usNPPId, currentDate } = props;

    return {
      currentDate,
      usNPPs: getUsNPPs(state, props),
      currentUSNPP: getCurrentUSNPP(state, props),
      goTo: props.goTo,
      usNPPId: props.usNPPId,
      params: props.params,
      production: getProduction(state),
      unavailabilities: getUnavailabilities(state),
      outage: getOutage(state),
      loading: getLoading(state),
      errors: getErrors(state),
    };
  };
};

const USNPPViewConnected = connect(makeMapStateToProps)(USNPPView);

export default USNPPViewWithRouter;
